"use client";

import { useEffect, useRef, useState } from "react";
import type { MarketPriceBand } from "@/features/pricing/shared/price-band";

// Fetches the market price band from /api/pris-estimat. `query` is null when
// the task isn't described well enough yet (or the surface shouldn't show a
// band), which resets the band without a network call.
//
// Responses are cached per query in a ref, so navigating back and forth
// between wizard steps (or dragging a slider back) doesn't re-hit the API.
// The cache only lives as long as the component is mounted; the route's
// s-maxage covers the rest.
//
// debounceMs > 0 is for surfaces where the query changes continuously (the
// calculator's sliders). The wizard only changes query on step transitions
// and uses 0.
export function usePriceBand(
  query: string | null,
  { debounceMs = 0 }: { debounceMs?: number } = {},
): { band: MarketPriceBand | null; loading: boolean } {
  const [band, setBand] = useState<MarketPriceBand | null>(null);
  const [loading, setLoading] = useState(false);
  const cache = useRef(new Map<string, MarketPriceBand | null>());

  useEffect(() => {
    if (!query) {
      setBand(null);
      setLoading(false);
      return;
    }

    const cached = cache.current.get(query);
    if (cached !== undefined) {
      setBand(cached);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      fetch(`/api/pris-estimat?${query}`, { signal: controller.signal })
        .then((res) => (res.ok ? res.json() : { band: null }))
        .then((data: { band: MarketPriceBand | null }) => {
          cache.current.set(query, data.band);
          setBand(data.band);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          // The price estimate isn't critical — on failure, show nothing.
          console.error("Prisestimat fejlede:", err);
          setBand(null);
          setLoading(false);
        });
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, debounceMs]);

  return { band, loading };
}
